import type { FC } from 'react';
import { useState } from 'react';
import Icon from '@components/Icon';
import { Show } from '@components/Show';
import {
  sidebarSectionChevronClasses,
  sidebarSectionIconClasses,
  sidebarSectionRowClasses,
  sidebarSectionWrapperClasses,
} from '@ContractBuilder/components/shared-sidebar';
import { NavSectionBlocks } from '@ContractBuilder/modules/navigation/modules/nav-section/views/NavSectionBlocks';
import { SectionTitle } from '@ContractBuilder/modules/navigation/modules/nav-section/views/SectionTitle';
import { useEntityStore } from '@ContractBuilder/store';
import { selectIsAnyMultiSelectActive, useMultiSelectStore } from '@ContractBuilder/store/multi-select.store';
import { useBlockHighlight } from '@ContractBuilder/utils/use-block-highlight';
import { SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ENDORSEMENT_DETAILS_SENTINEL } from '@domain/schemas/endorsement-summary.schema';
import { ChevronDownIcon, ChevronRightIcon, SwitchVerticalIcon as DragAndDropIcon } from '@heroicons/react/outline';
import { ArtificialCustomEvent, useCustomEventListener } from '@providers/events';
import { getSortedEntries, isNonNullish } from '@root/helpers';
import { getBlocksExcludedFromPreview } from '@root/helpers/blocks';
import { scrollIntoView } from '@root/src/utils/scroll-into-view';
import clsx from 'clsx';

import type { EndorsementDetailsNavItem } from '../../types';

const visibleOnHoverClasses = ['group-hover:opacity-100', 'opacity-0'];

interface NavEndorsementDetailsControllerProps {
  item: EndorsementDetailsNavItem;
  isDragDisabled: boolean;
}

export const NavEndorsementDetailsController: FC<NavEndorsementDetailsControllerProps> = ({ item, isDragDisabled }) => {
  const [open, setOpen] = useState(false);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: ENDORSEMENT_DETAILS_SENTINEL,
    disabled: isDragDisabled,
    data: { type: 'section' },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  useCustomEventListener(ArtificialCustomEvent.ToggleExpandSections, (value) => setOpen(value));

  const isAnyMultiSelectActive = useMultiSelectStore(selectIsAnyMultiSelectActive);
  const excludedBlockIds = useEntityStore(({ submission }) => getBlocksExcludedFromPreview(submission));

  const { onMouseEnter, onMouseLeave } = useBlockHighlight();

  const hasSubSections = item.endorsementDetailsSections.length > 0;
  const areAllChangesHidden =
    hasSubSections &&
    item.endorsementDetailsSections.every((section) =>
      section.blocks.every((block) => excludedBlockIds.includes(block.id)),
    );
  const isDimmed = !hasSubSections || areAllChangesHidden;
  const IconComponent = open ? ChevronDownIcon : ChevronRightIcon;

  return (
    <li ref={setNodeRef} style={style} {...attributes} id={`${ENDORSEMENT_DETAILS_SENTINEL}-draggable`}>
      <div className={clsx(sidebarSectionWrapperClasses(), 'mx-auto')}>
        <div
          className={sidebarSectionRowClasses({ interactive: hasSubSections })}
          onClick={() => hasSubSections && setOpen((prev) => !prev)}
          title={hasSubSections ? 'Click to expand endorsement details' : 'No endorsement details'}
        >
          <IconComponent
            className={sidebarSectionChevronClasses({ interactive: hasSubSections })}
            onClick={(e) => {
              e.stopPropagation();
              if (hasSubSections) setOpen((prev) => !prev);
            }}
          />
          <Icon name="material-changes" className={sidebarSectionIconClasses({ disabled: isDimmed })} />
          <SectionTitle title={item.label} isDimmed={isDimmed} />
          <Show when={isNonNullish(listeners) && !isDragDisabled}>
            <div
              className={clsx(
                'relative inline-flex items-center',
                isAnyMultiSelectActive && 'pointer-events-none opacity-30',
              )}
            >
              <div
                className={clsx(
                  'group-hover:text-info-800 h-6 w-6',
                  visibleOnHoverClasses,
                  !isAnyMultiSelectActive && 'cursor-grab active:cursor-grabbing',
                )}
                data-testid="nav-endorsement-details-drag-handle"
                {...(isAnyMultiSelectActive ? {} : listeners)}
              >
                <DragAndDropIcon className="h-6 w-6" />
              </div>
            </div>
          </Show>
        </div>

        {open && (
          <ol className="flex flex-col gap-1 pl-6">
            {item.endorsementDetailsSections.map((section) => {
              const sortedBlocks = getSortedEntries(section.layout, section.blocks);
              const blockIds = sortedBlocks.map((b) => b.id);
              const isSectionDimmed = sortedBlocks.every((block) => excludedBlockIds.includes(block.id));
              return (
                <li key={section.id} className="mt-1">
                  <div
                    className={sidebarSectionRowClasses({ interactive: false })}
                    onClick={() => scrollIntoView(section.id)}
                  >
                    <Icon name="layout" className={sidebarSectionIconClasses({ disabled: isSectionDimmed })} />
                    <SectionTitle title={section.label} isDimmed={isSectionDimmed} />
                  </div>
                  {sortedBlocks.length > 0 && (
                    <SortableContext items={blockIds} id={`endorsement-section-${section.id}`}>
                      <NavSectionBlocks
                        areOperationsHidden
                        blocks={sortedBlocks}
                        excludedBlockIds={excludedBlockIds}
                        isDragDisabled
                        shouldDisableOperations
                        onClick={scrollIntoView}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                        sectionId={section.id}
                      />
                    </SortableContext>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </li>
  );
};
